import React, { useState, useEffect, useRef, useContext } from "react";
import { Checkbox, Select, Tooltip, Icon, Modal } from "antd";
import {
    HazmatCheckboxIcon,
    PriorityIcon,
    SpecialIcon,
    IntermediateStorageIcon,
    IntermediateStorageCheckbox,
    SpecialCheckbox,
    PriorityCheckbox,
    HazmatCheckbox,
    VehicleTypeIcon,
    IntermediateStorageCheckboxSelected,
    SpecialCheckboxSelected,
    PriorityCheckboxSelected,
    HazmatCheckboxSelected,
    DefaultVehicleTypeIcon,
    FormCheckBoxIcon,
    PickupIcon
} from "../../common/CustomIcon";
import novehicle from "../../../assets/images/novehicle";
import InfoTooltip from "./InfoTooltip";
import { UserContext } from "../../context/UserContext";
import ColorTagSection from "../../components/orders/ColorTagSection";
import ReusableInput from "./ReusableInput";
import { formatFullName } from "../../helpers/common";
import { isLtlOrganization } from "../../helpers/orders";
import HazmatImg from "./HazmatImg";
import TruncatedText from "./TruncatedText";
import UN_Number from "./orders/UN_Number";
import UNNumberList from "../../common/UNNumberList";

const { Option } = Select;

const OptionsSection = React.memo(({
    updatedAccountId, 
    getDataRef, 
    orderData, 
    handleOptionsChange, 
    vehicleTypes = [], 
    hazmatClasses = [],
    UNNumbers = [],
    details={}, 
    availableSpace, 
    order_label_code, 
    screenMode = "", 
    conditionForOPnOD = false,
    orgSettings = {},
    isMultiDay,
    tags = [],
    setTags,
    bondedOrderChecked,
    setBondedOrderChecked,
    inboundChecked,
    customClearanceChecked,
    openInboundModal,
    openCustomClearanceModal,
    handleInboundCheckboxChange,
    handleCustomClearanceCheckboxChange,
    readyForCustomerChecked,
    setReadyForCustomerChecked,
    delivery_location_details,
    agentId,
    savedCustomClearanceDate,
    allDocsUploaded,
}) => {
    const { orgRules, currentOrg } = useContext(UserContext);
    const orderId = details?.id || details?.customer_order_id || details?._id;
    const deliveryLocation = delivery_location_details || details?.delivery_location_details || {};
    const customerName = formatFullName(deliveryLocation, "customer_first_name", "customer_last_name") ||
                         formatFullName(deliveryLocation, "first_name", "last_name") ||
                         "";
    const customerLabel = customerName ? `Ready for customer - ${customerName}` : "Ready for customer";
    const [showAllOptions, setShowAllOptions] = useState(false);
    const [vehicleType, setVehicleType] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedClass, setSelectedClass] = useState(""); 

    const [selectedUN, setSelectedUN] = useState("");
    const [selectedUNNumber, setSelectedUNNumber] = useState([]); 
    const [hazmatClass, setHazmatClass] = useState("");
    const [pkgGroup, setPkgGroup] = useState("");
    const [emergencyResponseNumber, setEmergencyResponseNumber] = useState("");
    const [isPlacard, setIsPlacard] = useState(false);
    const [description, setDescription] = useState("");
    const [editingUNIndex, setEditingUNIndex] = useState(null);

    const [selectedSubClass, setSelectedSubClass] = useState("");
    const [subClasses, setSubClasses] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isHazardous, setIsHazardous] = useState(false);
    const [waitTime, setWaitTime] = useState(0);
    const [open, setOpen] = useState(false);
    const [showUNModal, setShowUNModal] = useState(false)
    const [optionsArray, setOptionsArray] = useState([
        {
            label: "Intermediate Storage",
            checked: false,
            icon: <IntermediateStorageCheckbox />,
            payloadKey: "wh_storage",
            message:["Signifies that the order will need to be stored temporarily in a warehouse before delivery and applies any configured rates."],
            checkedIcon: <IntermediateStorageCheckboxSelected />,
            tooltipIcon: <IntermediateStorageIcon />,
        },
        {
            label: "Linehaul Special",
            checked: false,
            icon: <SpecialCheckbox />,
            payloadKey: "linehaul_special",
            message: ["Applies the 'Special Day Adjustment' rate to this linehaul."],
            checkedIcon: <SpecialCheckboxSelected />,
            tooltipIcon: <SpecialIcon />,
        },
        {
            label: "Priority",
            checked: false,
            icon: <PriorityCheckbox />,
            payloadKey: "priority",
            message:[ "Marks an order for priority delivery, ensuring it takes precedence along a route.  This is a standalone flag and not linked to rates."],
            checkedIcon: <PriorityCheckboxSelected />,
            tooltipIcon: <PriorityIcon />,
        },
    ]);
    const [hoveredField, setHoveredField] = useState(null);

    useEffect(() => {
        setShowAllOptions(availableSpace);
    }, [availableSpace]);

    useEffect(() => {
      if (screenMode === "full_edit") {
        if (details) {
          const updatedOptions = optionsArray.map((option) => ({
            ...option,
            checked: details[option.payloadKey] || false,
          }));
          const color_tags = _.get(details, "color_tags[0]", null) || null;
          const matchedClass = hazmatClasses?.find(c => c.class_name === details.hazmat_class);
          const matchedDiv = matchedClass?.sub_classes?.find(d => d.description === details.hazmat_division);
          setOptionsArray(updatedOptions);
          setVehicleType(details.vehicle_type || null);
          setSelectedColor(color_tags);
          setSelectedClass(matchedClass?.class_number || "");
          setSelectedSubClass(matchedDiv?.division || "");
          setIsHazardous(!!details.is_hazardous);
          setWaitTime(details.wait_time || 0);
          setSelectedUNNumber(details.un_numbers || []);
        }
      }
    }, [details, hazmatClasses]);

    useEffect(() => {
        if (getDataRef?.current) {
            getDataRef.current = () => ({
                ...optionsArray.reduce((acc, option) => {
                    acc[option.payloadKey] = option.checked;
                    return acc;
                }, {}),
                vehicle_type: vehicleType,
                color_tags: selectedColor,
                hazmat_class: selectedClass,
                hazmat_division: selectedSubClass,
            });
        }
    }, [optionsArray, vehicleType, selectedColor, getDataRef, selectedClass, selectedSubClass]);

    useEffect(() => {
        if (handleOptionsChange) {
            handleOptionsChange(optionsArray, vehicleType, selectedColor, selectedClass, selectedSubClass, isHazardous, waitTime, selectedUNNumber);
        }
    }, [optionsArray, vehicleType, handleOptionsChange, selectedColor, selectedClass, selectedSubClass, isHazardous, waitTime, selectedUNNumber]);

    useEffect(() => {
        const whStorageRequiredCodes = ['LH', 'RD', 'RT', 'OBT', 'CDK', 'WO', 'PCDK', 'CDKD','NLH','PLHD','PLH','LHD'];
        const specialCodes = ['R', 'D', 'TR', 'TD', 'MS', 'M'];
        const onlyPnD = ["OR", "OD"];

        let whStorageConfigValue = ["T", "AFW"].includes(order_label_code) && screenMode !== "full_edit" ? orgRules?.org_billing_rules?.enable_intermediate_storage_by_default === "true" : false;
        whStorageConfigValue = ["T", "AFW"].includes(order_label_code) && isMultiDay ? true : whStorageConfigValue;
        // LTL orgs default wh_storage checked for two-leg orders (T/AFW transfers + OR/OD 2-leg
        // pickup/delivery), kept editable — the disabled override below keeps OR/OD enabled for LTL.
        whStorageConfigValue = ["T", "AFW", "OR", "OD"].includes(order_label_code) && isLtlOrganization(currentOrg) ? true : whStorageConfigValue;

        const updatedOptions = optionsArray
          .filter((option) => {
              if (option.payloadKey === "linehaul_special" && (!['LH', 'NLH', 'PLH', 'LHD', 'PLHD'].includes(order_label_code) && !details?.is_lh_order)) return false;
            return true;
          })
          .map((option) => {
            if (option.payloadKey === "wh_storage") {
              return { 
                ...option,
                checked: whStorageRequiredCodes.includes(order_label_code) || whStorageConfigValue ? true : details[option.payloadKey],
                disabled: isMultiDay || whStorageRequiredCodes.includes(order_label_code) ||
                          specialCodes.includes(order_label_code) ||
                          (onlyPnD.includes(order_label_code) && conditionForOPnOD && !isLtlOrganization(currentOrg)),
              };
            }
            // if (option.payloadKey === "linehaul_special") {
            //   return {
            //     ...option,
            //     checked: details[option.payloadKey] || false,
            //     disabled: false,
            //   };
            // }
            return {
              ...option,
              checked: details[option.payloadKey] || false,
              disabled: false,
            };
          });
    
        setOptionsArray(updatedOptions);
    }, [order_label_code, conditionForOPnOD, isMultiDay, details, orgRules]);

    const handleCheckboxChange = (index) => {
        setOptionsArray((prev) =>
            prev.map((opt, i) =>
                i === index ? { ...opt, checked: !opt.checked } : opt
            )
        );
    };

    const handleVehicleTypeChange = (value) => {
        setVehicleType(value);
    };

    const toggleOptionsVisibility = () => {
        setShowAllOptions((prev) => !prev);
    };

    const handleDropdownChange = (openStatus) => {
        setOpen(openStatus);
    };   

    useEffect(() => {
        if (!selectedClass) {
            setSubClasses([]);
            return;
        }
        const cls = hazmatClasses?.find(c => c.class_number === selectedClass);
        setSubClasses(cls?.sub_classes || []);
        isEditing && setSelectedSubClass("");
    }, [selectedClass]);

    const handleClassChange = (val) => {
        setSelectedClass(val);
        setIsEditing(true);
    };

    const handleUNNumberChange = (val) => {
        setSelectedUNNumber(val);
        setIsEditing(true);
    };

    const handleSubClassChange = (val) => setSelectedSubClass(val);    

    const handleSaveUNNumber = () => {
        setShowUNModal(false);
    };

    const handleWaitTimeChange = (e) => {
        const value = Math.floor(e.target.value);
        if (value < 0) {
            setWaitTime(0);
            return;
        }
        setWaitTime(value);
    };

    const unSource = UNNumbers || [];

      const handleUNLinkClick = (index) => {
    const entry = selectedUNNumber[index];
    const data = typeof entry === 'string' ? { un_number: entry } : entry;
    setSelectedUN(data?.un_number);
    setHazmatClass(data.hazmat_class || "");
    setEmergencyResponseNumber(data.emergency_response_number || "");
    setPkgGroup(data.packing_group || "");
    setIsPlacard(data.is_placard_required === true || data.is_placard_required === "true");
    setDescription(data.description || "");
    setEditingUNIndex(index);
    setShowUNModal(true);
  };

  const handleAddUNClick = () => {
    setSelectedUN("")
    setHazmatClass("");
    setEmergencyResponseNumber("");
    setPkgGroup("");
    setIsPlacard(false);
    setDescription("");
    setEditingUNIndex(null);
    setShowUNModal(true);
  };

  const handleUNNumberSave = (unNumberData) => {
    setSelectedUNNumber(prev => {
      if (editingUNIndex !== null) {
        const updated = [...prev];
        updated[editingUNIndex] = unNumberData;
        return updated;
      }
      return [...prev, unNumberData];
    });
    setShowUNModal(false);
    setEditingUNIndex(null);
  };

  const removeUNNumber = (index) => {
    setSelectedUNNumber(prev => {
      const updated = prev.filter((_, i) => i !== index);

      if (updated.length === 0) {
        setIsHazardous(false);
      }

      return updated;
    });
  };

  const normalizeUNNumbers = (data) => {
    if (!data) return [];
    return data.map(entry =>
      typeof entry === 'string' ? { un_number: entry } : entry
    );
  };

      // Get the last occurrence index for each UN number
    const lastIndexMap = new Map();

    selectedUNNumber.forEach((val, index) => {
    const entry = typeof val === "string" ? { un_number: val } : val;
    if (entry?.un_number) {
        lastIndexMap.set(entry.un_number, index);
    }
    });

    return (
        <div className="OptionsContainer" style={{ marginBottom: "16px" }}>
            <div className="heading-3-large">Options</div>

            <div className="select-dropdown-notes" style={{ marginBottom: "8px" }} onMouseEnter={() => setHoveredField('waitTime')}
                onMouseLeave={() => setHoveredField(null)}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <label>Wait Time</label>
                    {hoveredField === 'waitTime' && (
                        <InfoTooltip
                            label="Wait Time"
                            icon={<Icon type="clock-circle" theme="twoTone" />}
                            message={[
                                "Enter the wait time in minutes. This is the time the driver will wait before starting the delivery.",
                            ]}
                        />
                    )}
                </div>
                <ReusableInput
                    data-testid="options-section"
                    value={waitTime}
                    onChange={handleWaitTimeChange}
                    placeholder="Enter Wait Time"
                    type="number"
                    size="middle"
                    suffix="min"
                />
            </div>

            {/* Vehicle Type */}
            <div className="select-dropdown-notes" style={{ marginBottom: "8px" }} onMouseEnter={() => setHoveredField('vehicleType')}
                onMouseLeave={() => setHoveredField(null)}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <label>Vehicle Type</label>
                    {hoveredField === 'vehicleType' && (
                        <InfoTooltip
                            label="Vehicle Type"
                            icon={<VehicleTypeIcon />}
                            message={["Select a vehicle type suitable for the order. Vehicle type affects delivery timing, costs, and other factors."]}
                        />
                    )}
                </div>
                <Select
                    data-testid="vehicle-type-dropdown"
                    value={vehicleType}
                    onChange={handleVehicleTypeChange}
                    suffixIcon={<Icon style={{ position: "right", color: "#576ec1" }} type="caret-down" />}
                    placeholder="Select Vehicle"
                    onDropdownVisibleChange={handleDropdownChange}
                    dropdownAlign={{ offset: [0, -35] }}
                    className="OptionsStyle"
                    style={{
                        border: "1px solid #8897a4",
                        borderRadius: "4px",
                        color: "#8897a4",
                    }}
                >
                    {vehicleTypes.map((vehicle) => (
                        <Option key={vehicle.code} value={vehicle.code}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    {vehicle.vehicle_image?.url ? (
                                        <img
                                            src={vehicle.vehicle_image.url}
                                            alt={vehicle.name}
                                            style={{
                                                width: "24px",
                                                height: "24px",
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                            }}
                                        />
                                    ) : (
                                        <DefaultVehicleTypeIcon
                                                styling={{
                                                    borderRadius: "4px",
                                                    width:"24",
                                                    height:"24"
                                                }}
                                        />
                                    )}
                                    <Tooltip title={vehicle.name} placement="right">
                                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {vehicle.name}
                                        </span>
                                    </Tooltip>
                          </div>
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Dispatch Color */}
            {orgSettings.require_dispatch_color === "true" && (
                <div className="select-dropdown-notes" style={{ marginBottom: "8px" }} onMouseEnter={() => setHoveredField('disptchColor')}
                    onMouseLeave={() => setHoveredField(null)}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <label>Dispatch Color</label>
                        {hoveredField === 'disptchColor' && (
                            <InfoTooltip
                                label="Dispatch Color"
                                icon={<PickupIcon />}
                                message={["Select a color for the order. This color is used to identify the order in the dispatch system and can be helpful for quick visual identification."]}
                            />
                        )}
                    </div>
                    <ColorTagSection 
                        setSelectedColor={setSelectedColor}
                        selectedColor={selectedColor}
                    />
                </div>
            )}

            {/* Hazmat Section */}
            <div style={{ marginBottom: "8px" }}>
                <div
                    className="select-dropdown-notes"
                    style={{ marginBottom: "8px" }}
                    onMouseEnter={() => setHoveredField('hazmat')}
                    onMouseLeave={() => setHoveredField(null)}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <label>
                                <Checkbox
                                    data-testid="hazmat-checkbox"
                                    className="OptionsStyle"
                                    checked={isHazardous}
                                    onChange={(e) => {
                                        setIsHazardous(e.target.checked)
                                        if (e.target.checked) {
                                            handleAddUNClick();
                                        } else {
                                            setSelectedUNNumber([]);
                                            setShowUNModal(false);
                                            setEditingUNIndex(null);
                                        }
                                        }}
                                >
                                    Hazmat
                                </Checkbox>
                        </label>
                        {hoveredField === 'hazmat' && (
                            <InfoTooltip
                                label="Hazmat"
                                icon={<HazmatCheckboxIcon />}
                                message={["Marks the order as containing hazardous materials (Hazmat) for dispatchers and drivers."]}
                            />
                        )}
                    </div>

                    {/* Show dropdowns only if checked */}
                    {isHazardous && (
                        <>
                       {orgRules?.org_billing_rules?.enable_UN_hazmat_fields === "true" ? 
                       <>
                          <UN_Number
                                visible={showUNModal}
                                onCancel={() => {
                                    setShowUNModal(false);
                                    setEditingUNIndex(false);
                                    if(!selectedUNNumber.length) {
                                        setIsHazardous(false);
                                    }
                                }}
                                onSave={handleUNNumberSave}
                                unSource={unSource}
                                unNumber={editingUNIndex !== null ? selectedUNNumber[editingUNIndex] : null}
                                selectedUN={selectedUN}
                                setSelectedUN={setSelectedUN}
                                hazmatClass={hazmatClass}
                                emergencyResponseNumber={emergencyResponseNumber}
                                description={description}
                                setSelectedUNNumber={setSelectedUNNumber}
                                setHazmatClass={setHazmatClass}
                                setEmergencyResponseNumber={setEmergencyResponseNumber}
                                setDescription={setDescription}
                                isEditing={editingUNIndex !== null}
                                pkgGroup={pkgGroup}
                                setPkgGroup={setPkgGroup}
                                isPlacard={isPlacard}
                                setIsPlacard={setIsPlacard}

                                // loading={saving}
                            />
                             <UNNumberList
                                selectedUNNumber={selectedUNNumber}
                                lastIndexMap={lastIndexMap}
                                unSource={unSource}
                                handleUNLinkClick={handleUNLinkClick}
                                removeUNNumber={removeUNNumber}
                                handleAddUNClick={handleAddUNClick}
                                isEditMode={true}
                              />
                        </> :
                         <>
                            <label>Hazmat Class <span style={{ color: "red" }}>*</span></label>
                            <Select
                                data-testid="hazmat-dropdown"
                                allowClear
                                value={selectedClass}
                                onChange={handleClassChange}
                                placeholder="Select Hazmat Class"
                                className="OptionsStyle"
                                style={{
                                    width: "100%",
                                    border: "1px solid #8897a4",
                                    borderRadius: "4px",
                                    color: "#8897a4",
                                    marginBottom: 8,
                                }}
                                suffixIcon={<Icon style={{ color: "#576ec1" }} type="caret-down" />}
                            >
                                {(hazmatClasses ?? []).map((cls) => (
                                    <Option key={cls.class_number} value={cls.class_number}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <HazmatImg hazmatClass={cls.class_number} />
                                            <TruncatedText text={cls.class_name} />
                                        </div>
                                    </Option>
                                ))}
                            </Select>

                            {selectedClass && (
                                <div>
                                    <label>Hazmat Division <span style={{ color: "red" }}>*</span></label>
                                    <Select
                                        data-testid="hazmat-division-dropdown"
                                        value={selectedSubClass}
                                        onChange={handleSubClassChange}
                                        placeholder="Select Sub-Class (Required)"
                                        status={!selectedSubClass ? "error" : ""}
                                        className="OptionsStyle"
                                        style={{
                                            width: "100%",
                                            border: "1px solid #8897a4",
                                            borderRadius: "4px",
                                            color: "#8897a4",
                                        }}
                                        suffixIcon={<Icon style={{ color: "#576ec1" }} type="caret-down" />}
                                    >
                                        {subClasses.map((sub) => (
                                            <Option key={sub.division} value={sub.division}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <HazmatImg hazmatClass={sub.division} />
                                                    <TruncatedText text={sub.description} />
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                    {!selectedSubClass && (
                                        <div style={{ fontSize: "12px", color: "#ff4d4f", marginTop: "4px" }}>
                                            Sub-class selection is required when Hazmat class is selected.
                                        </div>
                                    )}
                                </div>
                            )}
                        </>}
                        </>

                    )}
                </div>
            </div>

            {/* Other Checkboxes */}
            <div className="checkbox-container">
                {optionsArray
                  .filter(option => option.payloadKey !== "is_hazardous")
                  .slice(0, showAllOptions ? optionsArray.length : 1)
                  .map((option, index) => {
                    const actualIndex = optionsArray.indexOf(option);
                    return (
                      <React.Fragment key={actualIndex}>
                        <div
                          className="option-container"
                          onMouseEnter={() => setHoveredField(option.payloadKey)}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Checkbox
                              data-testid={`${option.label}-checkbox`}
                              className="OptionsStyle"
                              checked={option.checked}
                              onChange={() => handleCheckboxChange(actualIndex)}
                              disabled={option.disabled || false}
                            >
                              {option.checked ? option.checkedIcon : option.icon} &nbsp;
                              {option.label}
                            </Checkbox>

                            {hoveredField === option.payloadKey && (
                              <div style={{ marginLeft: "8px" }}>
                                <InfoTooltip
                                  label={option.label}
                                  icon={option.tooltipIcon}
                                  message={[option.message]}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Bonded Order checkbox under Intermediate Storage */}
                        {option.payloadKey === "wh_storage" && order_label_code === "PLH" && (
                          <div style={{ marginTop: "4px" }}>
                            <div className="option-container" style={{ display: "flex", alignItems: "center" }}>
                              <Checkbox
                                data-testid="bonded-order-checkbox"
                                className="OptionsStyle"
                                checked={!!bondedOrderChecked}
                                disabled={!!details?.inbound_order_numbers?.import_order_number}
                                onChange={(e) => {
                                  if (!details?.inbound_order_numbers?.import_order_number && setBondedOrderChecked) {
                                    setBondedOrderChecked(e.target.checked);
                                  }
                                }}
                              />
                              <span
                                className="OptionsStyle"
                                style={{ marginLeft: "8px", cursor: details?.inbound_order_numbers?.import_order_number ? "default" : "pointer" }}
                                onClick={() => {
                                  if (!details?.inbound_order_numbers?.import_order_number && setBondedOrderChecked) {
                                    setBondedOrderChecked(!bondedOrderChecked);
                                  }
                                }}
                              >
                                Bonded Order
                              </span>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                {orderId && inboundChecked && (
                  <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        data-testid="inbound-checkbox"
                        className="OptionsStyle"
                        checked={inboundChecked}
                        disabled={!!agentId}
                        onChange={(e) => {
                          if (!agentId && handleInboundCheckboxChange) {
                            handleInboundCheckboxChange(e.target.checked);
                          }
                        }}
                      >
                        Import
                      </Checkbox>
                      {inboundChecked && (
                        <Icon
                          type="edit"
                          style={{ marginLeft: "8px", cursor: "pointer", color: "#576ec1" }}
                          onClick={openInboundModal}
                          data-testid="edit-inbound-icon"
                        />
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        data-testid="custom-clearance-checkbox"
                        className="OptionsStyle"
                        checked={customClearanceChecked}
                        disabled={!inboundChecked || !!savedCustomClearanceDate}
                        onChange={(e) => {
                          if (!savedCustomClearanceDate && handleCustomClearanceCheckboxChange) {
                            handleCustomClearanceCheckboxChange(e.target.checked);
                          }
                        }}
                      >
                        Customs Clearance
                      </Checkbox>
                      {inboundChecked && customClearanceChecked && (
                        <Icon
                          type="edit"
                          style={{ marginLeft: "8px", cursor: "pointer", color: "#576ec1" }}
                          onClick={openCustomClearanceModal}
                          data-testid="edit-custom-clearance-icon"
                        />
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {inboundChecked && customClearanceChecked && (
                        <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                          <Checkbox
                            data-testid="ready-for-customer-checkbox"
                            className="OptionsStyle"
                            checked={readyForCustomerChecked}
                            disabled={details?.status === "COMPLETED" || !allDocsUploaded}
                            onChange={(e) => {
                              if (setReadyForCustomerChecked) {
                                setReadyForCustomerChecked(e.target.checked);
                              }
                            }}
                          >
                            {customerLabel}
                          </Checkbox>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Toggle Button */}
                {!availableSpace && optionsArray.length > 1 && (
                  <div
                      className="button-2-large"
                      onClick={toggleOptionsVisibility}
                      style={{ cursor: "pointer", color: "#576ec1", marginTop: "8px" }}
                  >
                      {showAllOptions ? (
                          <>Hide Options <Icon style={{ marginLeft: "8px", color: "#576ec1" }} type="caret-up" /></>
                      ) : (
                          <>See All Options <Icon style={{ marginLeft: "8px", color: "#576ec1" }} type="caret-down" /></>
                      )}
                  </div>
                )}
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.updatedAccountId === nextProps.updatedAccountId &&
        prevProps.orderData === nextProps.orderData &&
        prevProps.details === nextProps.details &&
        prevProps.availableSpace === nextProps.availableSpace &&
        prevProps.order_label_code === nextProps.order_label_code &&
        prevProps.vehicleTypes === nextProps.vehicleTypes &&
        prevProps.conditionForOPnOD === nextProps.conditionForOPnOD &&
        prevProps.isMultiDay === nextProps.isMultiDay &&
        prevProps.hazmatClasses === nextProps.hazmatClasses &&
        prevProps.bondedOrderChecked === nextProps.bondedOrderChecked &&
        prevProps.setBondedOrderChecked === nextProps.setBondedOrderChecked &&
        prevProps.inboundChecked === nextProps.inboundChecked &&
        prevProps.customClearanceChecked === nextProps.customClearanceChecked &&
        prevProps.openInboundModal === nextProps.openInboundModal &&
        prevProps.openCustomClearanceModal === nextProps.openCustomClearanceModal &&
        prevProps.handleInboundCheckboxChange === nextProps.handleInboundCheckboxChange &&
        prevProps.handleCustomClearanceCheckboxChange === nextProps.handleCustomClearanceCheckboxChange &&
        prevProps.tags === nextProps.tags &&
        prevProps.UNNumbers === nextProps.UNNumbers &&
        prevProps.readyForCustomerChecked === nextProps.readyForCustomerChecked &&
        prevProps.allDocsUploaded === nextProps.allDocsUploaded
    );
});

export default OptionsSection;
